import { getQueryParamsString } from 'src/dataLayer/localData/queryParams'
import { useAppConfigContext } from 'src/viewLayer/components/contexts/AppConfigContext'
import { useRouteParamsContext } from 'src/viewLayer/components/contexts/RouteParamsContext'

const useAboutBabbelBasePath = () => {
  return `https://about.babbel.com/${useRouteParamsContext().locale}/`
}

const useBasePath = () => {
  const { environment, locale } = useRouteParamsContext()
  const { devicePath } = useAppConfigContext()

  // when we visit /{locale}/authentication/ the path will be undefined
  // we need to handle undefined in here, otherwise it will redirect to
  // /{locale}/authentication/undefined/email
  const redirectionAppSlug = devicePath || 'login'
  return `/${locale}/${environment}/${redirectionAppSlug}/`
}

// a localised page exists only for german
const useBabbelBusinessPath = () => {
  const { locale } = useRouteParamsContext()
  return locale === 'de'
    ? 'https://www.babbelfuerunternehmen.de/pages/'
    : 'https://www.babbelforbusiness.com/pages/'
}

const useEmailPagePath = () => {
  const { queryParams } = useRouteParamsContext()
  return `${useBasePath()}email${getQueryParamsString(queryParams)}`
}

const usePasswordPagePath = () => {
  const { queryParams } = useRouteParamsContext()
  return `${useBasePath()}password${getQueryParamsString(queryParams)}`
}

const showMagicLink = () => {
  const params = new URLSearchParams(globalThis.location.search)
  return params.get('magic_link_enabled') === 'true'
}

const useForgotPasswordPagePath = (email) => {
  const { queryParams } = useRouteParamsContext()
  const { devicePath } = useAppConfigContext()
  // copy to avoid mutating the shared context instance used by the other path hooks
  const forgotPasswordQueryParams = new URLSearchParams(queryParams)
  if (email) {
    forgotPasswordQueryParams.set('email', email)
  }

  if (devicePath === 'login' || showMagicLink()) {
    forgotPasswordQueryParams.set('magic_link_enabled', true)
  }
  return `${useBasePath()}forgot-password${getQueryParamsString(forgotPasswordQueryParams)}`
}

const useBusinessPagePath = () => {
  const { queryParams } = useRouteParamsContext()
  return `${useBasePath()}business${getQueryParamsString(queryParams)}`
}

const useBusinessErrorPagePath = () => {
  return `${useBasePath()}business/error`
}

const usePrivacyPath = () => {
  return `${useAboutBabbelBasePath()}privacy`
}

const useTermsPath = () => {
  return `${useAboutBabbelBasePath()}terms`
}

// locales in array use the business privacy page, the other locales the default privacy page
const useBusinessPrivacyPath = () => {
  const { locale } = useRouteParamsContext()
  const businessPrivacyPath = `${useBabbelBusinessPath()}privacy-policy`
  const privacyPath = usePrivacyPath()
  return ['en', 'en_GB', 'pl'].includes(locale) ? businessPrivacyPath : privacyPath
}

// locales in array use localised business data page, the other locales the english business data page
const useBusinessDataPath = () => {
  const { locale } = useRouteParamsContext()
  const businessDataPath = `${useBabbelBusinessPath()}dse`
  return ['es', 'fr', 'it', 'pt'].includes(locale)
    ? `${businessDataPath}-${locale}`
    : businessDataPath
}

export {
  showMagicLink,
  useAboutBabbelBasePath,
  useBabbelBusinessPath,
  useBasePath,
  useBusinessDataPath,
  useBusinessErrorPagePath,
  useBusinessPagePath,
  useBusinessPrivacyPath,
  useEmailPagePath,
  useForgotPasswordPagePath,
  usePasswordPagePath,
  usePrivacyPath,
  useTermsPath,
}
